import { App as AntApp, ConfigProvider, theme } from 'antd';
import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import { Provider, useSelector } from 'react-redux';
import { BrowserRouter } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import App from './App';
import './index.css';
import { RootState, store } from './redux/store';
import { getAuth, onAuthStateChanged } from 'firebase/auth';
import { db } from './config/firebase';
import reportWebVitals from './reportWebVitals';
import { getUser } from './apis/apis';
import { setLogoDark, setLogoLight } from './redux/slices/logoSlice';
import './i18n/config';

const root = ReactDOM.createRoot(
  document.getElementById('root') as HTMLElement
);

const Main = () => {
  const isDarkMode = useSelector((state: RootState) => state.theme.isDarkMode);
  const [colorPrimary, setColorPrimary] = useState<string>('#0b82d5');
  const [backgroundColor, setBackgroundColor] = useState<string | undefined>(undefined);
  const auth = getAuth();

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      if (user) {
        const user = await getUser();
        const clientId = user?.clientId;
        if (clientId) {
          const clientDocRef = doc(db, 'Clients', clientId);
          const clientDoc = await getDoc(clientDocRef);
          if (clientDoc.exists()) {
            const clientData = clientDoc.data();

            if (clientData.lightColor) {
              //console.log('Color recuperado de Firestore:', clientData.lightColor);
              setColorPrimary(clientData.lightColor); // lightColor ya está en formato rgb
            }

            if (clientData.backgroundColor) {
              //console.log('Background color recuperado de Firestore:', clientData.backgroundColor);
              setBackgroundColor(clientData.backgroundColor);
            }

            if (clientData.logoDarkURL) {
              //console.log('Logo dark recuperado de Firestore:', clientData.logoDarkURL);
              store.dispatch(setLogoDark(clientData.logoDarkURL));
            }
            if (clientData.logoLightURL) {
              //console.log('Logo light recuperado de Firestore:', clientData.logoLightURL);
              store.dispatch(setLogoLight(clientData.logoLightURL));
            }
          }
        }

      }
    });

    // Cleanup subscription on unmount
    return () => unsubscribe();
  }, [auth]);

  return (
    <ConfigProvider
      theme={{
        algorithm: isDarkMode ? theme.darkAlgorithm : theme.defaultAlgorithm,
        token: {          
          colorPrimary: colorPrimary,
          // Aplicar backgroundColor solo en light mode
          ...(!isDarkMode && backgroundColor ? { colorBgBase: backgroundColor } : {}),
          // fontSize: 14, // Tamaño base para la tipografía global
          // fontSizeHeading1: 32, // Tamaño de Heading 1 (Title nivel 1)
          // fontSizeHeading2: 28, // Tamaño de Heading 2 (Title nivel 2)
          // fontSizeHeading3: 24, // Tamaño de Heading 3 (Title nivel 3)
          // fontSizeHeading4: 20, // Tamaño de Heading 4 (Title nivel 4)
          // fontSizeHeading5: 16, // Tamaño de Heading 5 (Title nivel 5)
        },
        components: {
          Button: {
            borderRadius: 100,            
          },
          Radio: {
            borderRadius: 100,
          },
          Select: {
            borderRadius: 100,
          },
          Input: {
            borderRadius: 100,
          },
          Typography: {           
            //fontSize: 16, // Tamaño general para Text y Paragraph
            //lineHeight: 1.5, // Altura de línea para mejorar la legibilidad
          },
          Rate: {
            starColor: colorPrimary, 
          }
        },
      }}
    >
      <BrowserRouter>
        <AntApp>
          <App />
        </AntApp>
      </BrowserRouter>
    </ConfigProvider>
  );
};

root.render(
  <React.StrictMode>
    <Provider store={store}>
      <Main />
    </Provider>
  </React.StrictMode>
);

reportWebVitals();
